import { useEffect, useState, type ReactNode, type RefObject } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CameraswitchRounded from "@mui/icons-material/CameraswitchRounded";
import CloudOffRounded from "@mui/icons-material/CloudOffRounded";
import FullscreenRounded from "@mui/icons-material/FullscreenRounded";
import LockRounded from "@mui/icons-material/LockRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import VideocamOffRounded from "@mui/icons-material/VideocamOffRounded";
import type { QrCameraStatus } from "../hooks/useQrCamera";
import { formatKioskTime } from "../lib/kioskResult";
import { KIOSK, kpx } from "./kioskTheme";

/** The facility's wall clock (the tablet may be set to another zone). */
function useClock(timeZone?: string | null): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  return formatKioskTime(now, timeZone);
}

function ChromeButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <ButtonBase
      aria-label={label}
      title={label}
      onClick={onClick}
      sx={{
        // 48 px: a comfortable touch target on a wall-mounted tablet.
        width: 48,
        height: 48,
        borderRadius: "50%",
        color: "inherit",
        opacity: 0.8,
        "&:hover, &:focus-visible": { opacity: 1, bgcolor: KIOSK.hover },
        "& svg": { fontSize: 20 },
      }}
    >
      {children}
    </ButtonBase>
  );
}

/**
 * The kiosk's top line: a lock, "Facility · Device", and the facility's time
 * (approved frames). Offline, camera switch and full screen sit quietly
 * beside the time.
 */
export function KioskStatusBar({
  place,
  timeZone,
  tone = "dark",
  offline = false,
  onSwitchCamera,
  onFullscreen,
}: {
  place: string;
  timeZone?: string | null;
  tone?: "dark" | "color";
  offline?: boolean;
  onSwitchCamera?: () => void;
  onFullscreen?: () => void;
}) {
  const clock = useClock(timeZone);
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 4,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: "26px",
        // 18 px above a 20 px line, as drawn; fixed so states never shift it.
        height: 56,
        boxSizing: "border-box",
        fontSize: 15,
        color: tone === "dark" ? KIOSK.text2 : KIOSK.onColor,
      }}
    >
      <LockRounded aria-hidden sx={{ fontSize: 20 }} />
      <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {place}
      </Box>
      <Box sx={{ flex: 1 }} />
      {offline ? (
        <Box
          component="span"
          role="status"
          sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: tone === "dark" ? KIOSK.alert : "inherit" }}
        >
          <CloudOffRounded aria-hidden sx={{ fontSize: 20 }} />
          Offline
        </Box>
      ) : null}
      {onSwitchCamera ? (
        <ChromeButton label="Switch camera" onClick={onSwitchCamera}>
          <CameraswitchRounded />
        </ChromeButton>
      ) : null}
      {onFullscreen ? (
        <ChromeButton label="Full screen" onClick={onFullscreen}>
          <FullscreenRounded />
        </ChromeButton>
      ) : null}
      <Box component="span" sx={{ fontFamily: KIOSK.mono, fontSize: 14, letterSpacing: "-.3px" }}>
        {clock}
      </Box>
    </Box>
  );
}

function Bracket({ corner }: { corner: "tl" | "tr" | "bl" | "br" }) {
  const top = corner[0] === "t";
  const left = corner[1] === "l";
  return (
    <Box
      component="i"
      aria-hidden
      sx={{
        position: "absolute",
        zIndex: 1,
        width: kpx(70),
        height: kpx(70),
        borderStyle: "solid",
        borderColor: KIOSK.accent,
        borderRadius: "6px",
        borderWidth: `${top ? kpx(6) : 0} ${left ? 0 : kpx(6)} ${top ? 0 : kpx(6)} ${left ? kpx(6) : 0}`,
        ...(top ? { top: kpx(22) } : { bottom: kpx(22) }),
        ...(left ? { left: kpx(22) } : { right: kpx(22) }),
      }}
    />
  );
}

/**
 * The 440 px scan window from the approved ready frame: corner brackets and a
 * glowing scan line over the live camera (mirrored for the front camera, so a
 * player sees their phone move the way they move it).
 */
export function KioskScanFrame({
  videoRef,
  cameraStatus,
  mirrored,
  checking,
  label,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  cameraStatus: QrCameraStatus;
  mirrored: boolean;
  checking: boolean;
  label: string;
}) {
  const live = cameraStatus === "scanning";
  const failed = cameraStatus === "blocked" || cameraStatus === "unavailable";
  return (
    <Box
      sx={{
        width: kpx(440),
        height: kpx(440),
        flex: "none",
        borderRadius: kpx(28),
        background: `radial-gradient(circle at 50% 40%, ${KIOSK.frameFrom}, ${KIOSK.frameTo})`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        muted
        playsInline
        autoPlay
        aria-label={label}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: mirrored ? "scaleX(-1)" : "none",
          opacity: live ? 0.9 : 0,
          transition: "opacity .3s",
        }}
      />
      {!live ? (
        <Box
          aria-hidden={!failed}
          role={failed ? "alert" : undefined}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            color: KIOSK.frameIcon,
            textAlign: "center",
            px: 5,
          }}
        >
          {failed ? (
            <>
              <VideocamOffRounded sx={{ fontSize: kpx(96), color: KIOSK.text3 }} />
              <Box sx={{ color: KIOSK.text2, fontSize: kpx(19), lineHeight: 1.4 }}>
                {cameraStatus === "blocked"
                  ? "Camera access is blocked. Allow the camera for this site in the browser settings, then reload."
                  : "No camera is available on this device."}
              </Box>
            </>
          ) : (
            <QrCode2Rounded sx={{ fontSize: kpx(120) }} />
          )}
        </Box>
      ) : null}
      <Bracket corner="tl" />
      <Bracket corner="tr" />
      <Bracket corner="bl" />
      <Bracket corner="br" />
      {!failed ? (
        <Box
          component="i"
          aria-hidden
          sx={{
            position: "absolute",
            zIndex: 1,
            left: kpx(40),
            right: kpx(40),
            top: "56%",
            height: 3,
            bgcolor: KIOSK.accent,
            boxShadow: `0 0 22px ${KIOSK.accent}`,
            opacity: 0.8,
            "@keyframes kioskScanLine": {
              "0%, 100%": { transform: `translateY(${kpx(-120)})` },
              "50%": { transform: `translateY(${kpx(90)})` },
            },
            animation: live && !checking ? "kioskScanLine 3.2s ease-in-out infinite" : "none",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      ) : null}
      {checking ? (
        <Box
          role="status"
          sx={{
            position: "absolute",
            zIndex: 1,
            left: "50%",
            bottom: kpx(40),
            transform: "translateX(-50%)",
            px: 2,
            py: 0.75,
            borderRadius: 999,
            bgcolor: KIOSK.scrim,
            color: KIOSK.white,
            fontSize: kpx(18),
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          Checking…
        </Box>
      ) : null}
    </Box>
  );
}

/** Full-screen root of every kiosk state. */
export function KioskSurface({
  background,
  children,
  label,
}: {
  background: string;
  children: ReactNode;
  label?: string;
}) {
  return (
    <Box
      component="main"
      aria-label={label}
      sx={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        bgcolor: background,
        color: KIOSK.white,
        fontFamily: KIOSK.font,
        WebkitFontSmoothing: "antialiased",
        userSelect: "none",
        touchAction: "manipulation",
      }}
    >
      {children}
    </Box>
  );
}
